import { env } from '@/config/env';
import { mapReport, type RawReport } from './report-mapper';
import { ScanError, type ScanAdapter, type ScanProgress } from './scan-adapter';
import type { ScanErrorCode } from './types';

interface StartResponse {
  accessToken?: string;
  scanId?: string;
}

/** One `data:` frame of GET /scan/progress. `connected` carries no progress. */
interface ProgressFrame {
  status?: string;
  message?: string;
  progress?: number;
  currentTask?: string;
}

const REPORT_TRIES = 4;
const REPORT_RETRY_MS = 1_000;
const DONE = new Set(['completed', 'complete', 'done', 'finished', 'success']);
const FAILED = new Set(['error', 'failed', 'failure']);

const base = () => env.vscannerApiUrl.replace(/\/+$/, '');

const sseUrl = (scanId: string) => `${base()}/scan/progress?scanId=${encodeURIComponent(scanId)}`;

// Progress comes over a WebSocket because the CDN in front of the API holds back an SSE stream
// and delivers it in one lump; WebSocket frames pass through one by one. SSE is the fallback.
function progressUrl(scanId: string): string {
  const url = new URL(`${base()}/scan/progress/ws?scanId=${encodeURIComponent(scanId)}`, window.location.href);
  url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';
  return url.toString();
}

function codeFromMessage(message: string): ScanErrorCode {
  if (/time.?out|timed out/i.test(message)) return 'TIMEOUT';
  if (/resolve|unreach|refused|connect|dns/i.test(message)) return 'UNREACHABLE';
  return 'UNKNOWN';
}

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        reject(new DOMException('Scan aborted', 'AbortError'));
      },
      { once: true },
    );
  });

async function start(host: string, signal: AbortSignal): Promise<Required<StartResponse>> {
  const res = await fetch(`${base()}/scan`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url: `https://${host}`, scan_type: 'full' }),
    signal,
  });
  if (res.status === 429) throw new ScanError('RATE_LIMIT');
  if (!res.ok) throw new ScanError(res.status >= 500 ? 'UNKNOWN' : codeFromMessage(await res.text()));
  const body = (await res.json()) as StartResponse;
  if (!body.scanId || !body.accessToken) throw new ScanError('UNKNOWN');
  return { scanId: body.scanId, accessToken: body.accessToken };
}

// The report can lag the last progress frame by a moment, so a 404 is retried.
export async function fetchReport(scanId: string, token: string, signal: AbortSignal): Promise<RawReport> {
  for (let attempt = 1; ; attempt++) {
    const res = await fetch(`${base()}/scan/report/${encodeURIComponent(scanId)}/`, {
      headers: { 'x-access-token': token },
      signal,
    });
    if (res.ok) return (await res.json()) as RawReport;
    if (attempt >= REPORT_TRIES || (res.status !== 404 && res.status < 500)) throw new ScanError('UNKNOWN');
    await wait(REPORT_RETRY_MS, signal);
  }
}

/** Resolves when the stream reports completion; rejects with a ScanError when it reports failure. */
function watchProgress(scanId: string, onProgress: (p: ScanProgress) => void, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let stream: { close(): void } | null = null;
    let settled = false;
    const settle = (done: () => void) => {
      if (settled) return;
      settled = true;
      stream?.close();
      signal.removeEventListener('abort', onAbort);
      done();
    };
    const onAbort = () => settle(() => reject(new DOMException('Scan aborted', 'AbortError')));
    signal.addEventListener('abort', onAbort, { once: true });

    const onData = (data: string) => {
      let frame: ProgressFrame;
      try {
        frame = JSON.parse(data) as ProgressFrame;
      } catch {
        return;
      }
      const status = (frame.status ?? '').toLowerCase();
      if (FAILED.has(status)) {
        settle(() => reject(new ScanError(codeFromMessage(frame.message ?? ''))));
        return;
      }
      if (typeof frame.progress === 'number') {
        onProgress({ percent: frame.progress, task: frame.currentTask ?? frame.message ?? '' });
      }
      if (DONE.has(status) || (typeof frame.progress === 'number' && frame.progress >= 100)) {
        settle(resolve);
      }
    };

    // Fallback for networks where the WebSocket cannot be opened at all.
    const useSse = () => {
      if (settled) return;
      const source = new EventSource(sseUrl(scanId));
      stream = source;
      source.onmessage = (event: MessageEvent<string>) => onData(event.data);
      // EventSource reconnects by itself on a dropped stream; only a closed one is final. The
      // server may also just end the stream when it is done, so settle by asking for the report.
      source.onerror = () => {
        if (source.readyState === EventSource.CLOSED) settle(resolve);
      };
    };

    let ws: WebSocket;
    try {
      ws = new WebSocket(progressUrl(scanId));
    } catch {
      useSse();
      return;
    }
    stream = ws;
    let opened = false;
    ws.onopen = () => {
      opened = true;
    };
    ws.onmessage = (event: MessageEvent<string>) => onData(event.data);
    // A WebSocket does not reconnect by itself. The server closes it when the scan is done, and
    // a dropped one cannot be resumed either way, so settle by asking for the report. One that
    // closed without ever opening was blocked, so read the stream over SSE instead.
    ws.onclose = () => {
      if (opened) settle(resolve);
      else useSse();
    };
  });
}

export const httpScanAdapter: ScanAdapter = {
  async run(host, onProgress, signal) {
    let scan: Required<StartResponse>;
    try {
      scan = await start(host, signal);
    } catch (err) {
      if (err instanceof ScanError || signal.aborted) throw err;
      throw new ScanError('UNKNOWN');
    }
    await watchProgress(scan.scanId, onProgress, signal);
    const report = await fetchReport(scan.scanId, scan.accessToken, signal);
    return { ...mapReport(report), ref: { scanId: scan.scanId, token: scan.accessToken } };
  },
};
